import { isValidLatLong } from './googlemaps';

/**
 * Geo helpers shared between the Findings tab and the Map editor. Pixel space is
 * the OpenLayers convention used to store positionables: origin bottom-left,
 * y pointing up.
 */

/** Parse a "lat, lng" location string into numbers, or null if it isn't one. */
export function parseLatLng(value) {
  if (typeof value !== 'string') return null;

  const m = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);

  if (!m) return null;

  const lat = Number(m[1]);
  const lng = Number(m[2]);

  return isValidLatLong({ latitude: lat, longitude: lng }) ? { lat, lng } : null;
}

/** Axis-aligned lat/lng bounds of a georeferenced projectUpload. */
function bounds(pu) {
  return {
    minLng: Math.min(pu.geo_top_left_lng, pu.geo_bottom_left_lng),
    maxLng: Math.max(pu.geo_top_right_lng, pu.geo_bottom_right_lng),
    minLat: Math.min(pu.geo_bottom_left_lat, pu.geo_bottom_right_lat),
    maxLat: Math.max(pu.geo_top_left_lat, pu.geo_top_right_lat),
  };
}

/** True when pu is georeferenced and the point lies inside its bounds. */
export function isLatLngInMap(lat, lng, pu) {
  if (!pu || pu.geo_top_left_lat == null) return false;
  const b = bounds(pu);

  return lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng;
}

/**
 * OL image pixel → [lng, lat] via bilinear interpolation over the four
 * calibrated corners. y is up: (0,0) = image bottom-left.
 */
export function pixelToLatLng(px, py, imgW, imgH, pu) {
  const u = px / imgW;
  const v = py / imgH;
  const lng =
    (1 - v) * ((1 - u) * pu.geo_bottom_left_lng + u * pu.geo_bottom_right_lng) +
    v * ((1 - u) * pu.geo_top_left_lng + u * pu.geo_top_right_lng);
  const lat =
    (1 - v) * ((1 - u) * pu.geo_bottom_left_lat + u * pu.geo_bottom_right_lat) +
    v * ((1 - u) * pu.geo_top_left_lat + u * pu.geo_top_right_lat);

  return [lng, lat];
}

/**
 * lat/lng → OL image pixel [x, y] (y up). Inverse of the axis-aligned bounds
 * mapping; matches the backend `latLngToPixelYUp` used for finding placement.
 */
export function latLngToPixel(lat, lng, imgW, imgH, pu) {
  const b = bounds(pu);

  if (b.maxLng === b.minLng || b.maxLat === b.minLat) return null;

  return [
    ((lng - b.minLng) / (b.maxLng - b.minLng)) * imgW,
    ((lat - b.minLat) / (b.maxLat - b.minLat)) * imgH,
  ];
}
